import { inject, Injectable, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import { Auth, authState, createUserWithEmailAndPassword, GoogleAuthProvider, signInAnonymously, signInWithEmailAndPassword, signInWithPopup, signOut, User, user, UserProfile } from '@angular/fire/auth';
import { collection, doc, DocumentSnapshot, Firestore, getDoc, setDoc, updateDoc } from '@angular/fire/firestore';
import { BehaviorSubject, filter, from, map, mergeMap, Observable, of, startWith, switchMap, tap } from 'rxjs';
import { RegistrationEvent, UserProfileUtil } from '../models/UserProfile';
import { isPlatformServer } from '@angular/common';
import { FstoreService } from './fstore.service';
import { UserSetting, UserSettingUtil } from '../models/UserSetting';
import { FstorageService } from './fstorage.service';
import { UserProfile as myProfile } from '../models/UserProfile';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public _auth = inject(Auth);
  private firestore = inject(Firestore);
  private fstore = inject(FstoreService);
  private fstorage = inject(FstorageService);

  public userProfileCollection = this.fstore.fscollection(UserProfileUtil.COLLECTION);
  public userSettingCollection = this.fstore.fscollection(UserSettingUtil.COLLECTION);

  user$ = user(this._auth);

  refreshUser$ = new BehaviorSubject(null);
  userProfile$ = this.refreshUser$.pipe(
    switchMap(()=>this.user$),
    switchMap(authUser=>this.getUserProfile(authUser?.uid)
      .pipe(map(localProfile=>{return {authUser,localProfile}}))
    ),
    tap(prof=>{
      if (prof.authUser && !prof.localProfile) {
        this.registerNewUser(prof as RegistrationEvent);
        // prof.localProfile = UserProfileUtil.create(prof.authUser) as any;
        // console.log('Just set the local profile for a new user', prof);
        // this.updateProfileFromSource(prof.localProfile);
        // if (prof.authUser.photoURL) {
        //   this.fstorage.uploadFileUrl(prof.authUser.uid, prof.authUser.photoURL).subscribe(uploadTask=>{
        //     console.log('Photo Uploaded:',prof?.authUser?.photoURL);
        //     uploadTask.then((snapshot=>{
        //       this.fstorage.getDownloadUrl(snapshot.ref).then(newPhotoUrl=>{
        //         console.log('Photo Uploaded, saving new url: ', newPhotoUrl);
        //         prof.localProfile['imageUrl'] = newPhotoUrl;
        //         this.updateProfileFromSource(prof.localProfile);
        //       });
        //     }))
        //   });
        // }
      }
    }),
    map(prof=>prof.localProfile as Partial<myProfile>)
  );

  userSetting$ = this.user$.pipe(
    switchMap(user=>this.getUserSetting(user?.uid)),
    filter(val=>!!val),
    // map(doc=>doc.data() as any as UserProfile),
    tap(prof=>console.log(prof),)
  );

  private readonly transferState = inject(TransferState);
  private readonly transferStateKey = makeStateKey<string|undefined>("auth:uid");
  protected readonly authState = authState(this._auth);
  public readonly uid$ = this.authState.pipe(map(u => u?.uid)).pipe(
    isPlatformServer(inject(PLATFORM_ID)) ?
        tap(it => this.transferState.set(this.transferStateKey, it)) :
        this.transferState.hasKey(this.transferStateKey) ?
          startWith(this.transferState.get(this.transferStateKey, undefined)) :
          tap()
  );

  //value = of('Uber User');

  constructor() { 
    //this._auth.onAuthStateChanged((user)=>this.onAuthenticated(user));
    // get a reference to the user-profile collection
    // this.userProfileCollection = collection(this.firestore, UserProfileUtil.COLLECTION);
  }

  onAuthenticated(user:User|null){
    if (user){
      console.log('User is authenticated',user);
      this.updateProfileFromSource(UserProfileUtil.create(user) as unknown as Partial<UserProfile>);
      // const profRef = doc(this.userProfileCollection,user.uid);
      // const newProfile = UserProfileUtil.create(user);
      // setDoc(profRef, newProfile);
    } else {
      console.log('No User is logged in');
    }
  }

  getUserProfile(id:string|undefined):Observable<Partial<myProfile>&{'id':string}>{
    return this.userProfileCollection.read<Partial<myProfile>&{id:string}>(id);
  }

  getUserSetting(id:string|undefined):Observable<Partial<UserSetting>>{
    return this.userSettingCollection.read<Partial<UserSetting>>(id).pipe(
      map(val=>val ?? {id})
    );

  }

  async logout() {
    return await signOut(this._auth);
  }

  async loginAnonymously() {
    return await signInAnonymously(this._auth);
  }

  async loginWithGoogle() {
    return await signInWithPopup(this._auth, new GoogleAuthProvider());
  }

  async loginWithEmail(email:string, password:string) {
    return await signInWithEmailAndPassword(this._auth, email, password);
  }

  async createUser(email:string, password:string){
    return await createUserWithEmailAndPassword(this._auth, email, password);
  }

  updateProfileFromSource(user:Partial<UserProfile>){
    return this.userProfileCollection.update(user['id']as string, user)
  }

  registerNewUser(prof:RegistrationEvent){
    prof.localProfile = UserProfileUtil.create(prof.authUser) as any;
    prof.localProfile.karma = 0;
    prof.localProfile.ideology = 'center';
    prof.localProfile.joinDate = new Date();
    console.log('Just set the local profile for a new user', prof);
    this.updateProfileFromSource(prof.localProfile);
    if (prof.authUser.photoURL) {
      this.fstorage.uploadFileUrl(prof.authUser.uid, prof.authUser.photoURL).subscribe(uploadTask=>{
        console.log('Photo Uploaded:',prof?.authUser?.photoURL);
        uploadTask.then((snapshot=>{
          this.fstorage.getDownloadUrl(snapshot.ref).then(newPhotoUrl=>{
            console.log('Photo Uploaded, saving new url: ', newPhotoUrl);
            prof.localProfile['imageUrl'] = newPhotoUrl;
            this.updateProfileFromSource(prof.localProfile);
          });
        }))
      });
    }

  }

}
